import { useCallback } from 'react'
import { useWatch } from 'react-hook-form'

import Box from '@mui/material/Box'
import Skeleton from '@mui/material/Skeleton'
import Switch from '@mui/material/Switch'
import Typography from '@mui/material/Typography'

import { CHANNEL_ID } from '~/consts'
import { useChannelLogisticsQuery } from '~/products/hooks/useChannelLogisticsQuery'
import { useProductFormContext } from '~/products/hooks/useProductForm'

const defaultValue = {
    channel: 'tiktok',
    value: [] as string[],
} as const

type Props = {
    shopId: number
}

export function TikTokDeliveryOptions({ shopId }: Props) {
    const name =
        `channel_attribute_values.${CHANNEL_ID.TikTok}.product.logistics.value.shop-${shopId}` as const
    const { data: deliveryOptionsData, status } = useChannelLogisticsQuery({
        channel: 'tiktok',
        shopId,
    })
    const { control, setValue, getValues } = useProductFormContext()
    const logisticAttributeValues = useWatch({
        control,
        name,
        defaultValue: {
            ...defaultValue,
        },
    })

    const toggleProvider = useCallback(
        (logisticId: string, checked: boolean) => {
            if (!deliveryOptionsData) {
                return
            }

            const currentLogisticAttributeValues =
                getValues(
                    `channel_attribute_values.${CHANNEL_ID.TikTok}.product.logistics.value.shop-${shopId}`
                ) ?? defaultValue

            if (currentLogisticAttributeValues.channel !== 'tiktok') {
                return
            }

            const validLogisticIds = new Set(deliveryOptionsData.map((option) => option.id))
            const currentValue = (currentLogisticAttributeValues.value ?? []).filter((id) =>
                validLogisticIds.has(id)
            )
            const nextValue = checked
                ? [...currentValue, logisticId]
                : currentValue.filter((id) => id !== logisticId)

            setValue(
                `channel_attribute_values.${CHANNEL_ID.TikTok}.product.logistics.value.shop-${shopId}`,
                {
                    ...currentLogisticAttributeValues,
                    value: nextValue,
                }
            )
        },
        [shopId, getValues, setValue, deliveryOptionsData]
    )

    if (status === 'loading') {
        return <Skeleton width={200} />
    }

    if (status === 'error') {
        return (
            <Typography color="error" variant="subtitle2">
                {gettext('Failed to load delivery options')}
            </Typography>
        )
    }

    if (logisticAttributeValues.channel !== 'tiktok') {
        return null
    }

    const existingIdSet = new Set(logisticAttributeValues.value)

    return (
        <Box sx={{ border: '1px solid', borderColor: 'grey.300', borderRadius: 1, p: 2 }}>
            {deliveryOptionsData.length ? (
                deliveryOptionsData.map(({ id, description }) => (
                    <Box
                        key={id}
                        sx={{
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'space-between',
                            width: '100%',
                        }}
                    >
                        <Typography variant="body2">{description}</Typography>
                        <Switch
                            color="secondary"
                            checked={existingIdSet.has(id)}
                            sx={{ flexShrink: 0 }}
                            onChange={(_, checked) => toggleProvider(id, checked)}
                        />
                    </Box>
                ))
            ) : (
                <Typography variant="body2">
                    {gettext(
                        'No custom delivery options enabled. Use default delivery option of current TikTok shop.'
                    )}
                </Typography>
            )}
        </Box>
    )
}
